// ==============================
// 翻訳方向
// ==============================
export type TranslationDirection = 'en-ja' | 'ja-en';

export const DIRECTION_LABELS: Record<TranslationDirection, { from: string; to: string; label: string }> = {
  'en-ja': { from: 'English', to: '日本語', label: 'English → 日本語' },
  'ja-en': { from: '日本語', to: 'English', label: '日本語 → English' },
};

// ==============================
// 接続状態
// ==============================
export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error'
  | 'reconnecting';

// ==============================
// サーバーから受信するメッセージ型
// ==============================
export interface StatusMessage {
  type: 'status';
  connected: boolean;
  connecting?: boolean;
  direction: TranslationDirection;
  paused?: boolean;
  captionPosition?: 'bottom' | 'top' | 'center';
  fontSize?: number;
  obsStyle?: string;
  showOriginal?: boolean;
  second?: string | null;
  testEndsAt?: number | null;
  endsAt?: number;
  hardEndsAt?: number;
  mode?: string;
}

export interface CaptionMessage {
  type: 'caption';
  interim: boolean;
  original: string;
  translation: string;
  direction: TranslationDirection;
  target?: string;
  lane?: number; // 2 = 2つ目の言語（同時表示）
  timestamp?: number;
}

export interface SecondMessage {
  type: 'second';
  value: string | null;
}

export interface DirectionMessage {
  type: 'direction';
  value: TranslationDirection;
}

export interface ErrorMessage {
  type: 'error';
  message: string;
}

export interface ReconnectingMessage {
  type: 'reconnecting';
  attempt: number;
  delay: number;
}

export type ServerMessage =
  | SecondMessage
  | StatusMessage
  | CaptionMessage
  | DirectionMessage
  | ErrorMessage
  | ReconnectingMessage
  | { type: 'clear' }
  | { type: 'paused' }
  | { type: 'resumed' }
  | { type: 'stopped' }
  | { type: 'speech_start' }
  | { type: 'speech_stop' }
  | { type: 'pong'; ts: number }
  | { type: 'position'; value: 'bottom' | 'top' | 'center' }
  | { type: 'fontSize'; value: number }
  | { type: 'obsStyle'; value: string }
  | { type: 'showOriginal'; value: boolean }
  // 時間管理メッセージ
  | { type: 'time_warning'; minsLeft: number; message: string }
  | { type: 'grace_entered'; message: string; extensionAvailable: boolean }
  | { type: 'time_expired'; message: string }
  | { type: 'test_timer'; endsAt: number }
  | { type: 'extend_applied'; expiresAt: number; hardExpiresAt: number; message: string }
  | { type: 'debug_event'; eventType: string };

// ==============================
// 翻訳履歴エントリ
// ==============================
export interface TranscriptEntry {
  id: string;
  timestamp: number;
  direction: TranslationDirection;
  target?: string;
  original: string;
  translation: string;
  sessionName: string;
}

// ==============================
// OBS表示スタイル
// ==============================
export type OBSStyleMode =
  | 'white-black'       // 白背景 + 黒文字（デフォルト）
  | 'transparent-black' // 透明背景 + 黒文字
  | 'transparent-white'; // 透明背景 + 白文字（黒縁）

export interface CaptionState {
  interim: boolean;
  original: string;
  translation: string;
  direction: TranslationDirection;
  target?: string;
}

// 訳す先の言語の表示名
export const TARGET_LABELS: Record<string, string> = {
  src: '原文', ja: '日本語', en: 'English', es: 'Español', de: 'Deutsch', fr: 'Français', it: 'Italiano',
  pt: 'Português', zh: '中文', ko: '한국어', th: 'ไทย', vi: 'Tiếng Việt', id: 'Bahasa Indonesia', tl: 'Filipino',
};
